import { useState, useContext } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, Rocket, CheckCircle, AlertTriangle, 
  Loader2, ExternalLink, Copy, Check
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { API, AuthContext } from "../../App";

const OnboardingPublish = () => {
  const navigate = useNavigate();
  const { onboardingData, refreshData } = useOutletContext();
  const { refreshUser } = useContext(AuthContext);
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(false);

  const photographer = onboardingData?.photographer;
  const user = onboardingData?.user;
  const stats = onboardingData?.stats;

  const brandName = photographer?.company_name;
  const slug = photographer?.slug;
  const canPublish = !!brandName;

  // Profile completeness check
  const completenessItems = [
    { label: "Brand name", done: !!brandName, required: true },
    { label: "Profile photo", done: !!user?.picture },
    { label: "Tagline", done: !!photographer?.tagline },
    { label: "Bio", done: !!photographer?.bio },
    { label: "Portfolio photos", done: (stats?.portfolio_photo_count || 0) > 0 },
    { label: "Stripe connected", done: photographer?.stripe_connect_status === "active" }
  ];

  const completedCount = completenessItems.filter(i => i.done).length;
  const completenessPercent = Math.round((completedCount / completenessItems.length) * 100);

  const handlePublish = async () => {
    if (!canPublish) {
      toast.error("Please add a brand name to publish your profile");
      return;
    }

    setPublishing(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(`${API}/onboarding/publish`, {}, { headers });
      
      toast.success("🎉 Your profile is now live!");
      
      // Refresh user data
      if (refreshUser) await refreshUser();
      
      // Redirect to the photographer's public page
      setTimeout(() => {
        window.location.href = response.data.profile_url;
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish profile");
      setPublishing(false);
    }
  };

  const handleBack = () => {
    navigate("/dashboard/onboarding/payments");
  };

  const copyProfileUrl = () => {
    const url = `${window.location.origin}/photographer/${slug}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Profile URL copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid="onboarding-step-5">
      <div className="mb-8 text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4"
        >
          <Rocket className="w-10 h-10 text-primary" />
        </motion.div>
        <h1 className="text-3xl font-serif mb-2">Ready to Launch!</h1>
        <p className="text-muted-foreground max-w-md mx-auto">
          Review your profile and publish it to start accepting bookings.
        </p>
      </div>

      <div className="space-y-6">
        {/* Profile Preview Card */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>Profile Preview</CardTitle>
            <CardDescription>
              Your profile URL: <span className="font-mono text-primary">/photographer/{slug || "your-brand"}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
              {user?.picture ? (
                <img
                  src={user.picture.startsWith("http") ? user.picture : `${API.replace("/api", "")}${user.picture}`}
                  alt={brandName}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                  <span className="text-2xl font-bold text-muted-foreground">
                    {brandName?.[0]?.toUpperCase() || "?"}
                  </span>
                </div>
              )}
              <div>
                <h3 className="text-xl font-serif">{brandName || "Your Brand Name"}</h3>
                {photographer?.tagline && (
                  <p className="text-muted-foreground">{photographer.tagline}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Completeness Card */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Profile Completeness</CardTitle>
              <Badge variant="outline">{completenessPercent}%</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {/* Progress Bar */}
            <div className="h-2 bg-muted rounded-full overflow-hidden mb-4">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${completenessPercent}%` }}
                className="h-full bg-primary"
              />
            </div>

            {/* Checklist */}
            <ul className="space-y-2">
              {completenessItems.map((item, i) => (
                <li key={i} className="flex items-center gap-2">
                  {item.done ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/30" />
                  )}
                  <span className={item.done ? "text-foreground" : "text-muted-foreground"}>
                    {item.label}
                    {item.required && <span className="text-red-500 ml-1">*</span>}
                  </span>
                </li>
              ))}
            </ul>

            {!canPublish && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
                <p className="text-sm text-red-400">
                  Brand name is required to publish your profile. 
                  <button 
                    className="underline ml-1"
                    onClick={() => navigate("/dashboard/onboarding")}
                  >
                    Go back to add it
                  </button>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* What Happens Next */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <h4 className="font-medium mb-3">What happens when you publish?</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-primary mt-0.5" />
                Your profile becomes visible at <span className="font-mono text-primary">/photographer/{slug}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-primary mt-0.5" />
                Clients can view your work and request bookings
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-primary mt-0.5" />
                You can continue editing your profile anytime from the dashboard
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex gap-3">
            {slug && (
              <Button variant="outline" onClick={copyProfileUrl}>
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy URL
                  </>
                )}
              </Button>
            )}
            <Button 
              onClick={handlePublish} 
              disabled={!canPublish || publishing}
              size="lg"
              className="min-w-[160px]"
              data-testid="publish-btn"
            >
              {publishing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Rocket className="w-4 h-4 mr-2" />
                  Publish Profile
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPublish;
